import clsx from 'clsx';
import React from 'react';

import Clipper from '@components/Clipper';
import { ClipperProps } from '@components/Clipper';
import Comments from '@components/Comments';
import MostPopularArticles from '@components/MostPopularArticles';
import SubscribeToNewsletter from '@components/Newsletter/SubscribeToNewsletter';
import RelatedStories from '@components/RelatedStories';
import SubscriberCouncil from '@components/SubscriberCouncil';
import { Env } from '@contexts/AppContext';
import useServerSideRendering from '@hooks/useServerSideRendering';
import { Article, MostPopularArticle, Pickup } from '@interfaces/homepage';
import { SubscriberCouncilPartner } from '@interfaces/partners';
import { CommentableType } from '@utils/backend-api/comments';

import ArticlePickups from '../ArticlePickups';
import Body from '../Body';
import { NewsletterProps } from '../interfaces';

interface ContentProps {
  addendum: Article['addendum'];
  authors: Article['authors'];
  bodyClassName?: string;
  canViewFullArticle: boolean;
  clipperProps: ClipperProps;
  commentCount?: number;
  env: Env;
  freeBlurb: Article['freeBlurb'];
  fullText: Article['fullText'];
  id: number;
  isGooglebot: boolean;
  mostPopularArticles: MostPopularArticle[];
  newsletter?: NewsletterProps;
  pickups?: Pickup[];
  relatedArticles: Article[];
  subscriberCouncil: SubscriberCouncilPartner[];
  takeaway: Article['takeaway'];
}

const Content = ({
  addendum,
  authors,
  bodyClassName,
  canViewFullArticle,
  clipperProps,
  commentCount,
  env,
  freeBlurb,
  fullText,
  id,
  isGooglebot,
  mostPopularArticles,
  newsletter,
  pickups,
  relatedArticles,
  subscriberCouncil,
  takeaway,
}: ContentProps): JSX.Element => {
  const hydrated = useServerSideRendering();

  return (
    <>
      <div
        className={clsx('flex flex-col md:flex-row md:gap-72 xl:gap-112', {
          'mb-80': !canViewFullArticle,
        })}
      >
        <div className="flex-1 md:ml-[60px] xl:ml-[84px]">
          <Body
            addendum={addendum}
            authors={authors}
            canViewFullArticle={canViewFullArticle}
            className={bodyClassName}
            freeBlurb={freeBlurb}
            fullText={fullText}
            isGooglebot={isGooglebot}
            takeaway={takeaway}
          />
          {canViewFullArticle && pickups?.length > 0 && <ArticlePickups pickups={pickups} />}
        </div>
        <div className="flex-shrink-0 md:w-[195px] lg:w-[260px]">
          {newsletter && <SubscribeToNewsletter {...newsletter} />}
          {mostPopularArticles.length > 0 && canViewFullArticle && (
            <MostPopularArticles
              className="mt-[260px] hidden md:block lg:pr-[30px] xl:mt-[218px]"
              mostPopularArticles={mostPopularArticles}
            />
          )}
        </div>
      </div>
      {canViewFullArticle && hydrated && (
        <div className="mr-0 mt-80 sm:mt-112 md:mr-[267px] lg:mr-[372px]">
          <Comments
            commentableId={id}
            commentableType={CommentableType.Article}
            commentCount={commentCount}
          />
        </div>
      )}
      <RelatedStories articles={relatedArticles} className="mb-80 sm:mb-112" />
      <SubscriberCouncil subscriberCouncil={subscriberCouncil} />
      {hydrated && <Clipper {...clipperProps} env={env} />}
    </>
  );
};

export default Content;
